import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search, FileText, Shield, Zap, HelpCircle, CreditCard, Users, Globe } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';

const faqData = [
  {
    category: "General Questions",
    icon: HelpCircle,
    questions: [
      {
        q: "What is LegalDocConverter?",
        a: "LegalDocConverter is an affordable document conversion and analysis platform designed for small business owners. We help you handle contracts, agreements, invoices, and business documents without paying expensive attorney fees. Convert between 30+ formats, get plain-English explanations of complex terms, and manage your business documents with confidence."
      },
      {
        q: "Who is LegalDocConverter for?",
        a: "LegalDocConverter is built specifically for small business owners, entrepreneurs, freelancers, startups, and anyone who deals with contracts and business documents. If you've ever been frustrated by expensive lawyer fees for simple document tasks, or felt confused by legal jargon in contracts, this tool is for you. We make legal documents accessible and affordable."
      },
      {
        q: "Is LegalDocConverter available internationally?",
        a: "Yes, LegalDocConverter is available worldwide. Our servers are located in North America with global CDN distribution for fast access from any location. We support multiple languages for OCR processing including English, French, German, Spanish, Italian, Portuguese, Dutch, Polish, Russian, Japanese, Chinese, Korean, Arabic, Hindi, and Thai. Our platform complies with international data protection standards including GDPR for European users."
      },
      {
        q: "How much can I save compared to hiring a lawyer?",
        a: "The average attorney charges $300-500 per hour for document review and basic legal work. With LegalDocConverter, you can convert, analyze, and understand your business documents for a fraction of that cost — often 90% or more in savings. Our Professional plan is just $19/month for unlimited access, compared to a single hour of lawyer time."
      }
    ]
  },
  {
    category: "Document Conversion",
    icon: FileText,
    questions: [
      {
        q: "What file formats do you support?",
        a: "We support conversion between over 30 document formats including: Word documents (DOC, DOCX), PDF (including PDF/A for archival), Rich Text Format (RTF), Plain Text (TXT), OpenDocument (ODT), HTML and web formats, Excel spreadsheets (XLS, XLSX), PowerPoint presentations (PPT, PPTX), EPUB for e-readers, Markdown, and many more. Our format support is regularly updated to include new standards and requirements."
      },
      {
        q: "What is PDF/A and do I need it?",
        a: "PDF/A is a special version of PDF designed for long-term storage. It ensures your document looks exactly the same years from now. You might need PDF/A if you're submitting documents to government agencies, keeping important business records, or filing documents with courts. We support all PDF/A standards and make conversion simple."
      },
      {
        q: "How accurate is your document conversion?",
        a: "Our conversion engine maintains extremely high fidelity to source documents. We preserve formatting, fonts, images, tables, headers and footers, page numbers, and document structure. Your contracts and business documents will look professional after conversion. We recommend a quick review of complex documents with intricate layouts."
      },
      {
        q: "Can I convert multiple documents at once?",
        a: "Yes, batch conversion is available for all registered users. Free tier users can process up to 3 documents per batch, while Professional subscribers enjoy unlimited batch processing. Perfect for converting entire folders of invoices, contracts, or business records at once."
      },
      {
        q: "What is the maximum file size you support?",
        a: "Free tier users can upload documents up to 10MB each. Professional subscribers can process files up to 100MB. This handles virtually any business document including lengthy contracts, detailed proposals, and comprehensive reports."
      }
    ]
  },
  {
    category: "OCR & Scanned Documents",
    icon: Search,
    questions: [
      {
        q: "What is OCR and how does it work?",
        a: "OCR (Optical Character Recognition) is technology that converts images of text—such as scanned paper documents or photographs—into editable, searchable text. Our OCR engine uses advanced machine learning trained on millions of documents to achieve accuracy rates exceeding 99% on clear text. We analyze document images, identify text regions, recognize individual characters, and reconstruct the document's text content while preserving layout information."
      },
      {
        q: "What languages does your OCR support?",
        a: "Our OCR engine supports 15 languages: English, French, German, Spanish, Italian, Portuguese, Dutch, Polish, Russian, Japanese, Chinese (Simplified and Traditional), Korean, Arabic, Hindi, and Thai. Multi-language document processing is supported, automatically detecting and handling language switches within documents. Additional language support is available for enterprise customers."
      },
      {
        q: "How should I prepare documents for best OCR results?",
        a: "For optimal OCR accuracy: scan at minimum 300 DPI resolution (400 DPI for small fonts), ensure documents are well-lit and evenly illuminated, align pages straight before scanning, use color or grayscale scanning rather than pure black-and-white for better contrast detection, ensure text is not cut off at edges, and avoid heavily creased or damaged originals when possible. Our preprocessing automatically corrects minor skew and enhances contrast, but starting with quality scans produces the best results."
      },
      {
        q: "Can OCR process handwritten documents?",
        a: "Our OCR includes handwriting recognition capabilities with varying accuracy depending on handwriting clarity. Block printed text converts more reliably than cursive script. Historical documents with archaic handwriting styles present greater challenges. We recommend handwritten text be verified after OCR processing. For documents mixing typed and handwritten text (such as filled forms), our engine handles both appropriately."
      }
    ]
  },
  {
    category: "Security & Privacy",
    icon: Shield,
    questions: [
      {
        q: "How do you protect my documents?",
        a: "Document security is our highest priority. All documents are encrypted in transit using TLS 1.3 and at rest using AES-256 encryption. Our infrastructure is hosted in SOC 2 Type II certified data centers. We implement strict access controls—only authorized systems can access stored documents. Regular security audits and penetration testing verify our protections. We never share, sell, or access your documents except as required to provide our services."
      },
      {
        q: "How long do you retain uploaded documents?",
        a: "Free tier documents are retained for 24 hours after upload, then automatically and permanently deleted. Professional subscribers can configure retention periods from immediate deletion to 90 days based on their workflow needs. Enterprise customers have custom retention options. Regardless of tier, you can manually delete any document at any time through your dashboard, with deletion being immediate and irreversible."
      },
      {
        q: "Is LegalDocConverter compliant with legal industry regulations?",
        a: "Yes, our platform is designed with legal industry compliance requirements in mind. We support GDPR compliance for European data subjects, maintain SOC 2 Type II certification for security controls, support HIPAA compliance for healthcare-related legal work (with appropriate BAA), and comply with data residency requirements for Canadian legal work. Our audit logging supports attorney professional responsibility requirements."
      },
      {
        q: "Can I use LegalDocConverter for confidential client documents?",
        a: "Absolutely. Our platform is designed for exactly this purpose. Attorney-client privilege and work product protection require secure document handling, which our platform provides through encryption, access controls, and audit logging. Many law firms use LegalDocConverter for their most sensitive matters. We recommend Professional tier for client work to benefit from enhanced security features and longer retention options."
      }
    ]
  },
  {
    category: "AI Document Analysis",
    icon: Zap,
    questions: [
      {
        q: "What can your AI document analysis do?",
        a: "Our AI-powered analysis provides intelligent insights into your legal documents including: document type classification, key clause and provision identification, risk factor highlighting, party and entity extraction, date and deadline identification, obligation mapping, cross-reference verification, and summary generation. The AI understands legal terminology and document structures, providing context-aware analysis rather than simple keyword matching."
      },
      {
        q: "Is AI analysis secure for confidential documents?",
        a: "Yes. Our AI processing occurs within our secure infrastructure using the same encryption and access controls as all document handling. We use enterprise-grade AI services with appropriate data processing agreements. Document content is not used to train AI models—your confidential information remains confidential. Analysis results are accessible only to you and are deleted according to your retention settings."
      },
      {
        q: "How accurate is the AI analysis?",
        a: "Our AI provides highly accurate analysis for common legal document types and standard provisions. However, AI analysis should supplement rather than replace attorney judgment. We recommend verifying AI-identified items, particularly for unusual document types or non-standard language. The AI continues improving through updated training, but ultimate responsibility for document review remains with the attorney."
      }
    ]
  },
  {
    category: "Pricing & Subscriptions",
    icon: CreditCard,
    questions: [
      {
        q: "What does the free tier include?",
        a: "Our free tier provides access to basic document conversion between common formats, limited OCR processing (up to 5 pages per document), basic PDF tools, and 24-hour document retention. Free users can upload documents up to 10MB and process up to 3 documents daily. This tier is ideal for occasional use or evaluating our platform before subscribing."
      },
      {
        q: "What additional features do Professional subscribers receive?",
        a: "Professional subscription ($19/month or $190/year) includes: unlimited document conversions and batch processing, full AI-powered document analysis, unlimited OCR with multi-language support, complete PDF toolkit access, real-time collaboration features, document version history, configurable retention up to 90 days, priority processing, up to 100MB file uploads, and dedicated email support. This tier is designed for regular professional use."
      },
      {
        q: "Do you offer enterprise or firm-wide pricing?",
        a: "Yes, we offer customized enterprise pricing for law firms and corporate legal departments with volume needs. Enterprise plans include custom user management, SSO integration, dedicated infrastructure options, custom retention policies, API access for workflow integration, dedicated account management, and SLA guarantees. Contact our sales team at enterprise@legaldocconverter.com for custom pricing."
      },
      {
        q: "Can I cancel my subscription anytime?",
        a: "Yes, you can cancel your Professional subscription at any time through your account dashboard. Cancellation takes effect at the end of your current billing period—you retain access to Professional features until then. We don't offer partial refunds for unused subscription time, but you're never locked into long-term commitments."
      }
    ]
  },
  {
    category: "Collaboration & Teams",
    icon: Users,
    questions: [
      {
        q: "Can I collaborate with colleagues on documents?",
        a: "Yes, Professional subscribers have access to real-time collaboration features. Multiple users can view and annotate documents simultaneously, with presence indicators showing who's currently active. Changes sync instantly across all connected sessions. Collaboration sessions are fully encrypted and logged for audit purposes."
      },
      {
        q: "How does document sharing work?",
        a: "You can share documents with other LegalDocConverter users through secure internal sharing, generating view-only or edit-enabled links. External sharing creates time-limited secure links that don't require recipient registration. All sharing is logged, and you can revoke access at any time. Shared documents remain under your control and count against your storage."
      },
      {
        q: "Can I track who accessed shared documents?",
        a: "Yes, comprehensive access logging tracks all document interactions including views, downloads, and edits. Access logs include timestamp, user identification, and action taken. These logs are available through your dashboard and can be exported for compliance documentation. Enterprise customers have additional reporting options."
      }
    ]
  },
  {
    category: "Technical & Integration",
    icon: Globe,
    questions: [
      {
        q: "What browsers and devices do you support?",
        a: "LegalDocConverter works on all modern browsers including Chrome, Firefox, Safari, and Edge (latest two versions of each). Our responsive design works on desktop, tablet, and mobile devices. For optimal experience, we recommend desktop browsers for complex document work and mobile for review and basic tasks. Native mobile apps are planned for future release."
      },
      {
        q: "Do you offer API access for integration?",
        a: "API access is available for Enterprise customers, enabling integration with document management systems, practice management software, and custom workflows. Our REST API supports all platform features including conversion, OCR, analysis, and document management. API documentation and sandbox environments are provided for development. Contact our enterprise team for API access."
      },
      {
        q: "Can I integrate LegalDocConverter with my existing software?",
        a: "Enterprise API enables integration with popular legal software including Clio, PracticePanther, MyCase, and document management systems like iManage and NetDocuments. We also support Zapier integration for connecting with thousands of other applications. Custom integrations can be developed through our API for specific workflow requirements."
      }
    ]
  }
];

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const filteredData = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const displayData = activeCategory !== null 
    ? [filteredData[activeCategory]].filter(Boolean)
    : filteredData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8">
            Find answers to common questions about LegalDocConverter's features, security, pricing, and more.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === null
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              All Categories
            </button>
            {faqData.map((category, index) => (
              <button
                key={index}
                onClick={() => setActiveCategory(index)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeCategory === index
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <category.icon className="h-4 w-4" />
                {category.category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {displayData.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No results found</h3>
              <p className="text-slate-500">Try adjusting your search or browse all categories.</p>
            </div>
          ) : (
            displayData.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <category.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">{category.category}</h2>
                </div>
                
                <div className="space-y-4">
                  {category.questions.map((item, questionIndex) => {
                    const key = `${categoryIndex}-${questionIndex}`;
                    const isOpen = openItems[key];
                    
                    return (
                      <div 
                        key={questionIndex}
                        className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden"
                      >
                        <button
                          onClick={() => toggleItem(categoryIndex, questionIndex)}
                          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                        >
                          <span className="font-medium text-slate-900 pr-4">{item.q}</span>
                          {isOpen ? (
                            <ChevronUp className="h-5 w-5 text-blue-600 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                          )}
                        </button>
                        
                        {isOpen && (
                          <div className="px-6 pb-4">
                            <p className="text-slate-600 leading-relaxed">{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-8">
            Can't find what you're looking for? Our support team is ready to help with any questions about LegalDocConverter.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact"
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
            >
              Contact Support
            </a>
            <a 
              href="/blog"
              className="px-8 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium transition-colors"
            >
              Read Our Blog
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
